// ReviewItemList — the "Needs your call (N)" group a change-set review
// renders above its normal table: every review item on the set, open ones
// first. A row opens the item in ReviewItemPanel. A hairline and an uppercase
// label, never a nested card (the delivery layout spec).
// SAD-768: when allowAddLine is set, an "Add a missing line" action stages a
// reviewer-authored CLIN onto the same set (money writes on apply).
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ReviewItemKind } from '@mono/proto-clients';
import { SpecraActionRow, SpecraBadge, SpecraButton, SpecraSectionHeader, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { ReviewItemGroupDrawer } from './ReviewItemGroupDrawer';
import { affectedLines, badgeLabel, isOpenReviewItem, kindKey, NO_TARGET_NAMES, resolutionLabel, reviewTitle, SETTLED_STATES, targetSummary } from './reviewItemCopy';
import { groupReviewItems, groupSummary, groupTitle, isOpenEntry, sectionByDocument } from './reviewItemGroups';
import { StageContractLineDrawer } from './StageContractLineDrawer';
// The section that leads a SOW set's review: the questions several SOWs
// raise on the same printed text, each asked once (M7).
const ACROSS_SOWS = 'Asked once across SOWs';
export function ReviewItemList({ items, periods = [], onOpen, allowAddLine = false, programId = '', changeSetId = '', targetNames = NO_TARGET_NAMES }) {
    const [addOpen, setAddOpen] = useState(false);
    const [openGroup, setOpenGroup] = useState(null);
    if (items.length === 0 && !allowAddLine) return null;
    // Items that ask one question read as one entry (reviewItemGroups); a group
    // is decided in its own drawer, which needs the set to write to. A set that
    // holds several documents' items reviews each document's together: the
    // set's own first, then each SOW reading's under its name (SAD-972). Text
    // several SOWs print alike is asked once, ahead of them (M7).
    const repeated = changeSetId ? groupReviewItems(items).filter((e)=>e.kind === 'group' && e.group.mode === 'repeat') : [];
    const inRepeat = new Set(repeated.flatMap((e)=>e.kind === 'group' ? e.group.members.map((m)=>m.id) : []));
    const perDocument = sectionByDocument(items.filter((d)=>!inRepeat.has(d.id))).map((s)=>{
        const entries = changeSetId ? groupReviewItems(s.items) : s.items.map((item)=>({
                kind: 'single',
                item
            }));
        return {
            label: s.label,
            entries,
            ordered: [
                ...entries
            ].sort((a, b)=>Number(isOpenEntry(b)) - Number(isOpenEntry(a)))
        };
    });
    const sections = repeated.length ? [
        {
            label: ACROSS_SOWS,
            entries: repeated,
            ordered: [
                ...repeated
            ].sort((a, b)=>Number(isOpenEntry(b)) - Number(isOpenEntry(a)))
        },
        ...perDocument
    ] : perDocument;
    const entries = sections.flatMap((s)=>s.entries);
    const open = entries.filter(isOpenEntry).length;
    // The open group is re-read from the current items so its members show
    // their latest state.
    const shownGroup = openGroup && (entries.find((e)=>e.kind === 'group' && e.group.id === openGroup.id)?.group ?? openGroup);
    return /*#__PURE__*/ _jsxs("section", {
        "data-testid": Testids.reviewItem.group,
        "data-count": open,
        "data-total": entries.length,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-start justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-0",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                variant: "group",
                                title: entries.length > 0 ? `Needs your call (${open})` : 'Line adjustments'
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                size: "meta",
                                tone: "muted",
                                className: "mt-[var(--space-1-5)]",
                                children: items.length > 0 ? "Facts Specra couldn't settle from the documents. Open one to decide it." : 'Add a line the documents printed that extraction missed.'
                            })
                        ]
                    }),
                    allowAddLine && programId && changeSetId && /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "secondary",
                        onClick: ()=>setAddOpen(true),
                        "data-testid": Testids.reviewItem.addMissingLine,
                        children: "Add a missing line"
                    })
                ]
            }),
            sections.map((section)=>/*#__PURE__*/ _jsxs("div", {
                    "data-testid": section.label ? Testids.reviewItem.documentSection : undefined,
                    "data-document": section.label || undefined,
                    "data-count": section.label ? section.entries.filter(isOpenEntry).length : undefined,
                    className: section.label ? 'mt-4' : undefined,
                    children: [
                        section.label && /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                            variant: "group",
                            title: `${section.label === ACROSS_SOWS ? ACROSS_SOWS : `SOW review · ${section.label}`} (${section.entries.filter(isOpenEntry).length})`
                        }),
                        section.ordered.length > 0 && /*#__PURE__*/ _jsx("div", {
                            className: "mt-2 divide-y divide-border/60",
                            children: section.ordered.map((entry)=>{
                                if (entry.kind === 'group') {
                                    const g = entry.group;
                                    const openCount = g.members.filter(isOpenReviewItem).length;
                                    const first = g.members[0].review;
                                    return /*#__PURE__*/ _jsx(SpecraActionRow, {
                                        className: "px-1",
                                        "data-testid": Testids.reviewItem.groupRow,
                                        "data-group-mode": g.mode,
                                        "data-count": g.members.length,
                                        "data-open": openCount,
                                        trailing: null,
                                        item: {
                                            id: g.id,
                                            onClick: ()=>setOpenGroup(g),
                                            label: /*#__PURE__*/ _jsx("span", {
                                                className: "font-medium",
                                                children: groupTitle(g, targetNames)
                                            }),
                                            description: groupSummary(g, targetNames),
                                            meta: /*#__PURE__*/ _jsxs("span", {
                                                className: "flex items-center gap-1.5",
                                                children: [
                                                    /*#__PURE__*/ _jsx(SpecraBadge, {
                                                        variant: first.kind === ReviewItemKind.IDENTITY ? 'info' : 'warning',
                                                        children: g.mode === 'teams' ? 'Confirm' : badgeLabel(first)
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraBadge, {
                                                        variant: openCount === 0 ? 'success' : 'danger',
                                                        children: openCount === 0 ? 'Settled' : openCount === g.members.length ? 'Blocks commit' : `${openCount} open`
                                                    })
                                                ]
                                            })
                                        }
                                    }, g.id);
                                }
                                const d = entry.item;
                                const r = d.review;
                                const lines = affectedLines(r);
                                const affected = lines.length;
                                const settled = SETTLED_STATES.has(d.resolutionState);
                                return /*#__PURE__*/ _jsx(SpecraActionRow, {
                                    className: "px-1",
                                    "data-testid": Testids.reviewItem.row,
                                    "data-item-id": d.id,
                                    "data-kind": kindKey(r.kind),
                                    "data-reason-code": r.reasonCode,
                                    "data-resolution": d.resolutionState,
                                    trailing: null,
                                    item: {
                                        id: d.id,
                                        onClick: ()=>onOpen(d.id),
                                        label: /*#__PURE__*/ _jsx("span", {
                                            className: "font-medium",
                                            children: reviewTitle(r)
                                        }),
                                        description: /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                targetSummary(d, periods, targetNames),
                                                affected > 0 && /*#__PURE__*/ _jsxs("span", {
                                                    "data-testid": Testids.reviewItem.appliesTo,
                                                    "data-count": affected,
                                                    children: [
                                                        ' ',
                                                        "· applies to ",
                                                        affected,
                                                        " sub-line",
                                                        affected === 1 ? '' : 's',
                                                        " (",
                                                        lines.slice(0, 6).join(', '),
                                                        affected > 6 ? ` and ${affected - 6} more` : '',
                                                        ")"
                                                    ]
                                                })
                                            ]
                                        }),
                                        // The badges ride in meta: the trailing slot is aria-hidden and dimmed.
                                        meta: /*#__PURE__*/ _jsxs("span", {
                                            className: "flex items-center gap-1.5",
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                                    variant: r.kind === ReviewItemKind.IDENTITY ? 'info' : 'warning',
                                                    children: badgeLabel(r)
                                                }),
                                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                                    variant: settled ? 'success' : 'danger',
                                                    children: settled ? resolutionLabel(d.resolutionState, r) : 'Blocks commit'
                                                })
                                            ]
                                        })
                                    }
                                }, d.id);
                            })
                        })
                    ]
                }, section.label || 'set')),
            changeSetId && /*#__PURE__*/ _jsx(ReviewItemGroupDrawer, {
                group: shownGroup,
                open: !!openGroup,
                onClose: ()=>setOpenGroup(null),
                changeSetId: changeSetId,
                names: targetNames,
                onOpenItem: (id)=>{
                    setOpenGroup(null);
                    onOpen(id);
                }
            }),
            allowAddLine && programId && changeSetId && /*#__PURE__*/ _jsx(StageContractLineDrawer, {
                open: addOpen,
                onOpenChange: setAddOpen,
                mode: "add",
                programId: programId,
                changeSetId: changeSetId
            })
        ]
    });
}
